import { MenuContent } from "./index";
import { useMenu } from "./useMenu";
import { useEffect, useRef, useState } from "react";

type DisplayDensity = "comfortable" | "compact" | "dense";

type DisplayFit = {
  columns: number;
  density: DisplayDensity;
  scale: number;
};

const densityWeights: Record<DisplayDensity, number> = {
  comfortable: 1,
  compact: 0.98,
  dense: 0.94
};

const MIN_READABLE_SCALE = 0.92;

type ViewportSize = {
  width: number;
  height: number;
};

function getViewportSize(): ViewportSize {
  const visualViewport = window.visualViewport;
  const width = visualViewport?.width || window.innerWidth || document.documentElement.clientWidth || 1280;
  const height = visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 720;

  return { width, height };
}

export function DisplayPage() {
  const { menu, status, error } = useMenu(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<DisplayFit>({ columns: 5, density: "comfortable", scale: 1 });
  const [viewport, setViewport] = useState<ViewportSize>(() =>
    typeof window === "undefined" ? { width: 1280, height: 720 } : getViewportSize()
  );

  useEffect(() => {
    const updateViewport = () => setViewport(getViewportSize());

    updateViewport();
    window.addEventListener("resize", updateViewport);
    window.visualViewport?.addEventListener("resize", updateViewport);

    return () => {
      window.removeEventListener("resize", updateViewport);
      window.visualViewport?.removeEventListener("resize", updateViewport);
    };
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || status === "loading") return;

    let frameId = 0;

    const chooseBaseColumnCount = () => {
      const usableWidth = viewport.width;
      if (usableWidth >= 1760) return 4;
      if (usableWidth >= 1360) return 4;
      if (usableWidth >= 1100) return 4;
      if (usableWidth >= 820) return 3;
      if (usableWidth >= 560) return 2;
      return 1;
    };

    const buildCandidates = () => {
      const baseColumns = chooseBaseColumnCount();
      const minColumns = viewport.width >= 1100 ? 2 : 1;
      const maxColumns = Math.min(4, Math.max(baseColumns, minColumns));
      const candidates: DisplayFit[] = [];

      for (let columns = minColumns; columns <= maxColumns; columns += 1) {
        candidates.push({ columns, density: "comfortable", scale: 1 });
        candidates.push({ columns, density: "compact", scale: 1 });
        candidates.push({ columns, density: "dense", scale: 1 });
      }

      return candidates;
    };

    const applyFit = (nextFit: DisplayFit) => {
      content.style.setProperty("--display-columns", String(nextFit.columns));
      content.style.setProperty("--display-scale", String(nextFit.scale));
      content.classList.toggle("menu-content--display-compact", nextFit.density === "compact");
      content.classList.toggle("menu-content--display-dense", nextFit.density === "dense");
    };

    const calculateFit = () => {
      const availableHeight = Math.max(240, viewport.height - content.getBoundingClientRect().top - 12);
      const candidates = buildCandidates();
      let finalFit = candidates[0];
      let bestScore = Number.NEGATIVE_INFINITY;

      for (const candidate of candidates) {
        applyFit(candidate);

        const unscaledHeight = Math.max(content.scrollHeight, 1);
        const heightRatio = availableHeight / unscaledHeight;
        const scale = Math.max(MIN_READABLE_SCALE, Math.min(1.3, heightRatio * 0.985));
        const visualHeight = unscaledHeight * scale;
        const fillRatio = Math.min(1, visualHeight / availableHeight);
        const cardWidth = (viewport.width / candidate.columns) * scale;
        const readability = scale * densityWeights[candidate.density];
        const score = readability * 8 + fillRatio * 3 + Math.min(cardWidth / 560, 1.5) - candidate.columns * 0.18;

        if (visualHeight <= availableHeight + 2 && score > bestScore) {
          bestScore = score;
          finalFit = { ...candidate, scale };
        }
      }

      if (bestScore === Number.NEGATIVE_INFINITY) {
        const fallback = candidates[candidates.length - 1];
        applyFit(fallback);
        const scale = Math.max(MIN_READABLE_SCALE, Math.min(1, (availableHeight / Math.max(content.scrollHeight, 1)) * 0.985));
        finalFit = { ...fallback, scale };
      }

      applyFit(finalFit);
      setFit((currentFit) =>
        currentFit.columns === finalFit.columns &&
        currentFit.density === finalFit.density &&
        Math.abs(currentFit.scale - finalFit.scale) < 0.01
          ? currentFit
          : finalFit
      );
    };

    const scheduleFit = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(calculateFit);
    };

    scheduleFit();
    window.addEventListener("resize", scheduleFit);
    window.visualViewport?.addEventListener("resize", scheduleFit);
    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(scheduleFit);
    resizeObserver?.observe(content);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", scheduleFit);
      window.visualViewport?.removeEventListener("resize", scheduleFit);
      resizeObserver?.disconnect();
    };
  }, [menu, status, viewport]);

  return (
    <main className="page-shell page-shell--display">
      <header className="display-header">
        <p className="eyebrow">Chozen Yogurt</p>
        <h1>Bus Menu</h1>
      </header>
      <MenuContent
        menu={menu}
        status={status}
        error={error}
        displayMode
        contentRef={contentRef}
        displayColumns={fit.columns}
        displayDensity={fit.density}
        displayScale={fit.scale}
      />
    </main>
  );
}
